/**
 * Signup intent: the role template, tests and plan a prospect chose on the
 * marketing site, carried through registration so the first assessment can
 * start from that choice instead of an empty setup.
 *
 * Pure functions only. Storage access is injected so this runs in Node tests.
 * Values are sanitised again on the server; nothing here is trusted alone.
 */

export const INTENT_STORAGE_KEY = 'tc_signup_intent'
// A week: long enough to confirm the email later, short enough not to linger (20 Oct 2026).
export const INTENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
export const INTENT_MAX_TESTS = 6

const SLUG = /^[a-z0-9][a-z0-9-]{0,79}$/

const cleanSlug = value => (typeof value === 'string' && SLUG.test(value) ? value : null)

const cleanSlugList = value => {
  const list = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',') : [])
  const seen = []
  for (const item of list) {
    const slug = cleanSlug(typeof item === 'string' ? item.trim() : item)
    if (slug && !seen.includes(slug)) seen.push(slug)
    if (seen.length >= INTENT_MAX_TESTS) break
  }
  return seen
}

/**
 * Build an intent from a route query such as
 * ?template=finance-quickbooks&test=quickbooks-114e8e&plan=essential
 *
 * @param {Object} query
 * @param {Number} now
 * @return {Object|null}
 */
export function intentFromQuery (query, now = Date.now()) {
  if (!query || typeof query !== 'object') return null
  const intent = {}
  const role = cleanSlug(query.role)
  if (role) intent.role = role
  const template = cleanSlug(query.template)
  if (template) intent.template = template
  const plan = cleanSlug(query.plan)
  if (plan) intent.plan = plan
  const tests = cleanSlugList(query.tests !== undefined ? query.tests : query.test)
  if (tests.length) intent.tests = tests
  if (!Object.keys(intent).length) return null
  intent.capturedAt = new Date(now).toISOString()
  return intent
}

/**
 * Normalise anything that claims to be an intent (server response, storage).
 *
 * @param {*} value
 * @param {Number} now
 * @return {Object|null}
 */
export function normaliseIntent (value, now = Date.now()) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const intent = {}
  const role = cleanSlug(value.role)
  if (role) intent.role = role
  const template = cleanSlug(value.template)
  if (template) intent.template = template
  const plan = cleanSlug(value.plan)
  if (plan) intent.plan = plan
  const tests = cleanSlugList(value.tests)
  if (tests.length) intent.tests = tests
  if (!Object.keys(intent).length) return null
  const captured = Date.parse(value.capturedAt)
  intent.capturedAt = Number.isFinite(captured) ? new Date(captured).toISOString() : new Date(now).toISOString()
  if (now - Date.parse(intent.capturedAt) > INTENT_MAX_AGE_MS) return null
  if (value.consumedAt) intent.consumedAt = value.consumedAt
  return intent
}

/**
 * The server keeps only verified values. Prefer them, but keep a plan the
 * server did not echo (it is only used to highlight a plan).
 *
 * @param {Object|null} local
 * @param {Object|null} server
 */
export function mergeIntent (local, server, now = Date.now()) {
  const a = normaliseIntent(local, now)
  const b = normaliseIntent(server, now)
  if (!a) return b
  if (!b) return a
  const merged = { ...a, ...b }
  // A fresh role link replaces a previous test/template choice, and vice versa.
  if (b.role) {
    delete merged.template
    delete merged.tests
  } else if (b.template || b.tests) {
    delete merged.role
  }
  if (!b.plan && a.plan) merged.plan = a.plan
  merged.capturedAt = a.capturedAt < b.capturedAt ? a.capturedAt : b.capturedAt
  return normaliseIntent(merged, now)
}

/** Strip client-only fields before sending to the API. */
export function intentForServer (intent) {
  const clean = normaliseIntent(intent)
  if (!clean) return null
  const out = {}
  if (clean.role) out.role = clean.role
  if (clean.template) out.template = clean.template
  if (clean.plan) out.plan = clean.plan
  if (clean.tests) out.tests = clean.tests
  return out
}

export function readIntent (storage, now = Date.now()) {
  try {
    const raw = storage && storage.getItem(INTENT_STORAGE_KEY)
    if (!raw) return null
    const intent = normaliseIntent(JSON.parse(raw), now)
    if (!intent) storage.removeItem(INTENT_STORAGE_KEY)
    return intent
  } catch (e) {
    return null
  }
}

export function writeIntent (storage, intent, now = Date.now()) {
  const clean = normaliseIntent(intent, now)
  try {
    if (!clean) { storage.removeItem(INTENT_STORAGE_KEY); return null }
    storage.setItem(INTENT_STORAGE_KEY, JSON.stringify(clean))
  } catch (e) {
    // Storage denied or full: the intent still lives in memory for this page.
  }
  return clean
}

export function clearIntent (storage) {
  try { storage.removeItem(INTENT_STORAGE_KEY) } catch (e) {}
}

/**
 * Human wording for what was chosen, using catalogue names when available.
 *
 * @param {Object|null} intent
 * @param {Object} names  { templates: {slug: name}, tests: {slug: name}, plans: {slug: name} }
 * @return {Array<string>}
 */
export function describeIntent (intent, names = {}) {
  const clean = normaliseIntent(intent)
  if (!clean) return []
  const label = (kind, slug) => (names[kind] && names[kind][slug]) || slug.replace(/-[0-9a-f]{5,6}$/, '').replace(/-/g, ' ')
  const parts = []
  if (clean.role) parts.push(label('roles', clean.role))
  if (clean.template) parts.push(label('templates', clean.template))
  if (clean.tests) parts.push(...clean.tests.map(slug => label('tests', slug)))
  if (clean.plan) parts.push(`${label('plans', clean.plan)} plan`)
  return parts
}

/**
 * The tests to propose, in order: the template battery first, then any
 * individual tests not already in it. Unknown slugs are dropped.
 *
 * @param {Object|null} intent
 * @param {Object|null} template  verified template from the API
 * @param {Array} catalogue      verified exams [{slug, name, minutes, format, includeInFreePlan}]
 * @return {Array}
 */
export function proposedExams (intent, template, catalogue = []) {
  const clean = normaliseIntent(intent)
  if (!clean) return []
  const bySlug = {}
  for (const exam of catalogue || []) if (exam && exam.slug) bySlug[exam.slug] = exam
  const out = []
  const push = exam => { if (exam && !out.some(e => e.slug === exam.slug) && out.length < INTENT_MAX_TESTS) out.push(exam) }
  if (template && clean.template === template.slug) for (const exam of template.exams || []) push(exam)
  for (const slug of clean.tests || []) push(bySlug[slug])
  return out
}

export function totalMinutes (exams) {
  return (exams || []).reduce((sum, exam) => sum + (Number.isFinite(exam.minutes) ? exam.minutes : 0), 0)
}

/**
 * The role a new workspace's first assessment is made for: only the signed-in
 * employer's own server record, saved at sign-up. It survives the confirmation
 * link opening a fresh page (guy+officejuniior, 20 Oct 2026), and it can never
 * be another account's: a role this browser remembers may come from someone
 * else's sign-up on the same machine (Guy's Bright Ideas workspace, 20 Oct).
 *
 * @param {Object|null} employer the signed-in employer (signupIntent from the server)
 * @return {String|null} a role slug, or null
 */
export function firstAssessmentRole (employer) {
  const intent = employer && employer.signupIntent
  return intent && typeof intent === 'object' ? cleanSlug(intent.role) : null
}

/**
 * What the assessment builder starts from. Only what the address asks for:
 * ?role= (a role link, or the lifecycle mail), or ?first=1, the first screen
 * of a new workspace, which starts from that employer's own saved selection.
 * "Create assessment" anywhere else always opens step 1 (Guy, 20 Oct 2026).
 *
 * @param {Object} query the route query
 * @param {Object|null} employer the signed-in employer
 * @return {Object|null} an intent for the builder, or null
 */
export function builderIntent (query, employer) {
  const role = cleanSlug(query && query.role)
  if (role) return { role }
  if (query && query.first === '1' && employer && employer.signupIntent) {
    const saved = normaliseIntent(employer.signupIntent)
    if (saved) {
      delete saved.capturedAt
      return saved
    }
  }
  return null
}

/**
 * The "Continue with your website selection" banner on the Assessments page.
 * Only for the signed-in employer's own saved selection of a template or
 * tests, and only while their workspace has no assessment at all. Never a role:
 * a role's assessment is made for them on the first screen. So a later
 * "Create assessment" by the same employer, or anyone signing in to another
 * workspace in this browser, never sees it (Guy, 20 Oct 2026).
 *
 * @param {Object|null} employer the signed-in employer
 * @param {Object|null} total {active, archived} from the assessments list, or null before it loads
 * @return {Object|null} the selection to offer, or null
 */
export function selectionBanner (employer, total) {
  if (!employer || !total || !Number.isFinite(total.active) || !Number.isFinite(total.archived)) return null
  if (total.active + total.archived > 0) return null
  const saved = normaliseIntent(employer.signupIntent)
  if (!saved || saved.role || (!saved.template && !saved.tests)) return null
  delete saved.capturedAt
  return saved
}
